import {
  getAllSnackOrdersOptions,
  snackSupplierOptions,
  useBuilding,
} from "@/api";
import { PrintableOrders } from "@/components/snack/PrintableOrders";
import SnackOrdersTimeFilter from "@/components/snack/SnackOrdersTimeFilter";
import SortableTableHeader from "@/components/table/SortableTableHeader";
import Table from "@/components/table/Table";
import { useCommonSnackTableColumns } from "@/hooks/useCommonSnackTableColumns";
import { useLocalState } from "@/hooks/useLocalState";
import useSnackOrderStates from "@/hooks/useSnackOrderStates";
import { useUsersMappings } from "@/hooks/useUsersMappings";
import { useWelcomeSnackAccess } from "@/hooks/useWelcomeSnackAccess";
import {
  EXCEL_NOK_FORMAT,
  EXCEL_PERCENTAGE_FORMAT,
  firebaseTimestampToDate,
  getSelectedItemsInDisplayOrder,
  isFromToDateFilter,
  isSnackOrderWithServiceType,
  isValidFirebaseTimestamp,
  snackStatusColorMap,
  snackStatusSortMap,
  ZONED_ISO_DATE,
} from "@/lib";
import {
  downloadArrayAsCSV,
  downloadArrayAsXlsx,
  SNACK_ORDER_PRINT_STYLES,
} from "@/lib";
import {
  PrintableSnackOrder,
  SnackOrder,
  SnackOrderStatus,
  SnackTimeframeFilter,
  SnackTimeframeFilterValue,
} from "@/types";
import {
  Button,
  Checkbox,
  Dropdown,
  DropdownMenu,
  InputField,
  Option,
} from "@app-components";
import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import {
  createColumnHelper,
  RowSelectionState,
  Table as ReactTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import { useRouterStore } from "@/store/zustandStore";
import { useBuildingQuery } from "@/components/RouterSetup";
import { OrderLifecycleHint } from "@/components/snack/OrderLifecycleSummary";
import { OrderReadError } from "@/components/snack/OrderReadError";
import { snackOrderStatusKey } from "@/lib/snackOrderLifecycle";

type SearchableColumns = keyof SnackOrder;
const searchableColumns: Array<SearchableColumns> = [
  "userFirstName",
  "userLastName",
  "userEmail",
  "companyName",
];

type SnackOrderTableRow = SnackOrder & { selectCol?: string };
const columnHelper = createColumnHelper<SnackOrderTableRow>();

export const Route = createFileRoute("/_portal/snack-admin/orders/")({
  component: RouteComponent,
  loader: () => {
    const { queryClient, supplierCompanyId } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.prefetchQuery(
      getAllSnackOrdersOptions(
        supplierCompanyId,
        SnackTimeframeFilter.NEXT_30_DAYS,
      ),
    );
  },
});

function RouteComponent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const router = useRouter();
  const { allowSnackAdminAccess } = useWelcomeSnackAccess();
  const { data: building } = useBuilding();
  const { data: supplier } = useBuildingQuery(
    snackSupplierOptions(building?.supplierCompanyId),
  );
  const printComponentRef = useRef<HTMLDivElement>(null);
  const { resetOrderStates } = useSnackOrderStates();

  const [timeframeFilter, setTimeframeFilter] =
    useLocalState<SnackTimeframeFilterValue>(
      "snackAdminTimeframeFilterV2",
      SnackTimeframeFilter.NEXT_30_DAYS,
    );
  const {
    data: orders,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useBuildingQuery(
    getAllSnackOrdersOptions(building?.supplierCompanyId, timeframeFilter),
  );
  const {
    usersOrgNrMap,
    companiesOrgNrMap,
    usersPhoneNrMap,
    userIdToSubdivisionNameMap,
  } = useUsersMappings(orders, {
    phoneNrs: true,
    subdivisionNames: true,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useLocalState<
    Option<SnackOrderStatus | "__ALL__">[]
  >("snackAdminStatusFilter", [
    { label: t("SNACK_ADMIN.STATUS_FILTER_ALL"), value: "__ALL__" },
  ]);
  const [filteredOrders, setFilteredOrders] = useState<SnackOrder[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const tableInstanceRef = useRef<ReactTable<SnackOrderTableRow> | null>(null);

  const handleTableReady = useCallback(
    (table: ReactTable<SnackOrderTableRow>) => {
      tableInstanceRef.current = table;
    },
    [],
  );

  const [pdfExportOrders, setPdfExportOrders] = useState<PrintableSnackOrder[]>(
    [],
  );
  const exportFileName = isFromToDateFilter(timeframeFilter)
    ? `orders-${timeframeFilter.from}_${timeframeFilter.to}`
    : `orders-${timeframeFilter}`;

  // Clears selected rows when filter criteria change to prevent exporting stale or hidden selections
  useEffect(() => {
    setRowSelection({});
  }, [timeframeFilter, searchTerm, statusFilter]);

  useEffect(() => {
    const includeAllStatuses = statusFilter.some((o) => o.value === "__ALL__");
    // Only filter if we have orders AND have a searchterm or are filtering on specific statuses
    if (!!orders?.length && (searchTerm || !includeAllStatuses)) {
      const statusesToInclude = includeAllStatuses
        ? []
        : statusFilter.map((s) => s.value);
      const searchTerms = searchTerm.toLowerCase().split(" ");
      setFilteredOrders(
        orders.filter(
          (order: SnackOrder) =>
            (searchTerm
              ? searchTerms.every((term) =>
                  searchableColumns.some((column) =>
                    (
                      order[column as keyof SnackOrder]
                        ?.toString()
                        .toLowerCase() || ""
                    ).includes(term.toLowerCase()),
                  ),
                )
              : true) &&
            (includeAllStatuses
              ? true
              : statusesToInclude.includes(order.status.current)),
        ),
      );
    } else {
      setFilteredOrders(orders ?? []);
    }
  }, [searchTerm, orders, statusFilter]);

  const handlePrint = useReactToPrint({
    contentRef: printComponentRef,
    documentTitle: `${exportFileName}.pdf`,
    pageStyle: SNACK_ORDER_PRINT_STYLES,
  });

  const handleExport = (type?: "excel" | "pdf") => {
    // Export specifically selected rows if any are checked; otherwise fall back to exporting all filtered rows
    const selectedOrders = getSelectedItemsInDisplayOrder(
      tableInstanceRef.current,
    );
    const targetOrders =
      selectedOrders.length > 0 ? selectedOrders : filteredOrders;

    if (type === "pdf") {
      const exportData = targetOrders.map((order) => ({
        ...order,
        userPhone: usersPhoneNrMap.get(order.userId) || "",
      }));
      setPdfExportOrders(exportData);
      setTimeout(() => {
        handlePrint();
      }, 10);
    } else {
      const headers = [
        "id",
        "orderNumber",
        "userId",
        "userEmail",
        "userFirstName",
        "userLastName",
        "companyId",
        "companyName",
        "subdivision",
        "orgNumber",
        "items",
        "deliveryType",
        "subtotalExclVat",
        "deliveryFeeExclVat",
        "totalExclVat",
        "vatRate",
        "vatAmount",
        "totalInclVat",
        "invoiceReference",
        "deliveryAddress",
        "deliveryDate",
        "deliveryTime",
        "status",
      ];
      const exportData = targetOrders.map((order) => {
        const hasVat = isSnackOrderWithServiceType(order.deliveryType);
        const subtotalExclVat = order.subtotal;
        const totalExclVat = hasVat
          ? (order.netAmount ?? order.subtotal + order.deliveryFee)
          : order.totalAmount;
        // order.totalAmount is already gross (netAmount + vatAmount)
        const totalInclVat = hasVat ? order.totalAmount : undefined;
        return {
          ...order,
          subdivision: userIdToSubdivisionNameMap.get(order.userId) || "",
          orgNumber:
            usersOrgNrMap.get(order.userId) ||
            companiesOrgNrMap.get(order.companyId) ||
            "",
          subtotalExclVat,
          deliveryFeeExclVat: order.deliveryFee,
          totalExclVat,
          totalInclVat,
          status: t(
            snackOrderStatusKey(order.status.current, order.lifecycleMode),
          ),
          deliveryDate: isValidFirebaseTimestamp(order.deliveryTime)
            ? order.deliveryTime
            : null,
          deliveryTime: isValidFirebaseTimestamp(order.deliveryTime)
            ? order.deliveryTime
            : null,
        };
      });
      const customValueHandlers = {
        items: (items: SnackOrder["items"]) =>
          items
            .map(
              (item) =>
                `${item.name[i18n.language === "en" ? "en" : "no"]} (x${item.quantity})`,
            )
            .join(" | "),
        status: (status: string) => status,
        invoiceReference: (
          invoiceReference: SnackOrder["invoiceReference"],
        ) => {
          if (!invoiceReference) return "";
          if (typeof invoiceReference === "string") return invoiceReference;
          return Object.values(invoiceReference).join(" - ");
        },
        deliveryDate: (deliveryTime: SnackOrder["deliveryTime"] | null) =>
          deliveryTime
            ? ZONED_ISO_DATE.format(firebaseTimestampToDate(deliveryTime))
            : "—",
        deliveryFeeExclVat: Number,
        subtotalExclVat: Number,
        totalExclVat: Number,
        totalInclVat: (total: SnackOrder["totalAmount"]) =>
          total != null ? Number(total) : undefined,
      };
      if (type === "excel") {
        downloadArrayAsXlsx(
          exportData,
          exportFileName,
          headers,
          customValueHandlers,
          {
            subtotalExclVat: EXCEL_NOK_FORMAT,
            deliveryFeeExclVat: EXCEL_NOK_FORMAT,
            totalExclVat: EXCEL_NOK_FORMAT,
            totalInclVat: EXCEL_NOK_FORMAT,
            vatRate: EXCEL_PERCENTAGE_FORMAT,
            vatAmount: EXCEL_NOK_FORMAT,
          },
        );
      } else {
        downloadArrayAsCSV(
          exportData,
          exportFileName,
          headers,
          customValueHandlers,
        );
      }
    }
  };

  const { deliveryTimeColumn, locationColumn } = useCommonSnackTableColumns();
  const columns = useMemo(
    () => [
      // Checkbox column for row selection with select-all header
      columnHelper.accessor("selectCol", {
        cell: ({ row }) => {
          const handler = row.getToggleSelectedHandler();
          return (
            <Checkbox
              label="Select row"
              hideLabel
              checked={row.getIsSelected()}
              onChange={handler}
              onClick={(e) => e.stopPropagation()}
              style={{
                container: { display: "unset" },
                checkbox: { top: "unset" },
              }}
            />
          );
        },
        header: ({ table }) => {
          return (
            <Checkbox
              label="Select all"
              hideLabel
              checked={table.getIsAllPageRowsSelected()}
              nativeOnChange={table.getToggleAllPageRowsSelectedHandler()}
              style={{
                container: { display: "unset" },
                checkbox: { top: "unset" },
              }}
            />
          );
        },
        meta: {
          excludeFromColumnsToggler: true,
        },
      }),
      columnHelper.accessor("status.current", {
        id: "status",
        cell: (info) => {
          const status = info.getValue();
          const dotColor =
            status === SnackOrderStatus.ORDERED
              ? "var(--wlcm-color-warning-orange)"
              : snackStatusColorMap[status];
          return (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <p
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--wlcm-spacing-xs)",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: 20,
                    background: dotColor,
                  }}
                ></span>
                <span>
                  {t(
                    snackOrderStatusKey(
                      status,
                      info.row.original.lifecycleMode,
                    ),
                  )}
                </span>
              </p>
              {/* Aligned under the label, past the 10px status dot */}
              <div
                style={{
                  paddingLeft: "calc(10px + var(--wlcm-spacing-xs))",
                  whiteSpace: "nowrap",
                }}
              >
                <OrderLifecycleHint order={info.row.original} />
              </div>
            </div>
          );
        },
        header: (info) => <SortableTableHeader info={info} label="Status" />,
        sortingFn: (rowA, rowB) => {
          return (
            snackStatusSortMap[rowA.original.status.current] -
            snackStatusSortMap[rowB.original.status.current]
          );
        },
        meta: { label: "Status" },
      }),
      deliveryTimeColumn,
      locationColumn,
      columnHelper.accessor(
        ({ userFirstName, userLastName, userEmail }) => ({
          userFirstName,
          userLastName,
          userEmail,
        }),
        {
          id: "customer",
          cell: (info) => {
            const { userFirstName, userLastName, userEmail } = info.getValue();
            return (
              <>
                {userFirstName} {userLastName}
                <span
                  style={{ marginLeft: "var(--wlcm-spacing-xs)", opacity: 0.5 }}
                >
                  ({userEmail})
                </span>
              </>
            );
          },
          header: t("SNACK_ADMIN.TABLE.CUSTOMER"),
          meta: { label: t("SNACK_ADMIN.TABLE.CUSTOMER") },
        },
      ),
      columnHelper.accessor("companyName", {
        cell: (info) => info.getValue(),
        header: t("SNACK_ADMIN.TABLE.COMPANY"),
        meta: { label: t("SNACK_ADMIN.TABLE.COMPANY") },
      }),
      columnHelper.accessor("items", {
        cell: (info) =>
          info.getValue()?.reduce((acc, item) => acc + item.quantity, 0) ?? "",
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("SNACK_ADMIN.TABLE.PRODUCTS")}
          />
        ),
        sortingFn: (rowA, rowB) => {
          const aItems = rowA.original.items.reduce(
            (acc, item) => acc + item.quantity,
            0,
          );
          const bItems = rowB.original.items.reduce(
            (acc, item) => acc + item.quantity,
            0,
          );
          return aItems - bItems;
        },
        meta: { label: t("SNACK_ADMIN.TABLE.PRODUCTS") },
      }),
      columnHelper.accessor("participantsCount", {
        cell: (info) => info.getValue() || "-",
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("SNACK_ADMIN.TABLE.PARTICIPANTS_COUNT")}
          />
        ),
        meta: { label: t("SNACK_ADMIN.TABLE.PARTICIPANTS_COUNT") },
      }),
    ],
    [t, deliveryTimeColumn, locationColumn],
  );

  if (!allowSnackAdminAccess) return null;

  const selectedOrdersCount = Object.keys(rowSelection).length;

  return (
    <>
      <div className="pageHeader">
        <h1>{t("PAGES.SNACK_ADMIN.HEADER")}</h1>
        <p>
          {t("PAGES.SNACK_ADMIN.SUBHEADER", {
            buildingName: building?.displayName,
          })}
        </p>
      </div>
      {!!error && (
        <OrderReadError
          retry={refetch}
          isFetching={isFetching}
          hasData={!!orders}
        />
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap-reverse",
          gap: "var(--wlcm-spacing-xs) var(--wlcm-spacing-md)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0px var(--wlcm-spacing-sm)",
          }}
        >
          <InputField
            compact
            hideLabel
            label={t("SEARCH")}
            placeholder={t("SNACK_ADMIN.TABLE.SEARCH")}
            icon="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SnackOrdersTimeFilter
            value={timeframeFilter}
            onChange={setTimeframeFilter}
          />
          <Dropdown
            compact
            isMulti
            label="Status"
            hideLabel
            value={statusFilter.map((option) => ({
              ...option,
              label:
                option.value === "__ALL__"
                  ? t("SNACK_ADMIN.STATUS_FILTER_ALL")
                  : t(
                      snackOrderStatusKey(
                        option.value,
                        supplier?.lifecycleMode,
                      ),
                    ),
            }))}
            // Delivered exists only in the active PAY-505 lifecycle; a legacy
            // deployment keeps its existing filter options.
            options={Object.values(SnackOrderStatus)
              .filter(
                (key) =>
                  key !== SnackOrderStatus.DELIVERED ||
                  supplier?.lifecycleMode === "delivery-closure-v1",
              )
              .map((key) => ({
                label: t(snackOrderStatusKey(key, supplier?.lifecycleMode)),
                value: key,
              }))}
            onChange={(options) => {
              if (!options) return;
              setStatusFilter(options as Option<SnackOrderStatus>[]);
            }}
            showSelectAllOption
            selectAllLabel={t("SNACK_ADMIN.STATUS_FILTER_ALL")}
          />
        </div>
        <Button
          icon="add"
          onClick={() => {
            resetOrderStates();
            navigate({ to: "/snack-admin/orders/new" });
          }}
        >
          {t("SNACK_ADMIN.NEW_ORDER.HEADER")}
        </Button>
      </div>
      {(!error || orders) && (
        <Table
          name="all-food-orders"
          columns={columns}
          data={filteredOrders}
          isLoading={isLoading}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          onTableReady={handleTableReady}
          getRowClassName={(order) =>
            order.status.current === SnackOrderStatus.CANCELLED ||
            order.status.current === SnackOrderStatus.DENIED
              ? "strikethrough"
              : ""
          }
          onRowClick={(order) =>
            navigate({
              to: "/snack-admin/orders/$orderId",
              params: { orderId: order.id },
            })
          }
          onRowHover={(order) => {
            router.preloadRoute({
              to: "/snack-admin/orders/$orderId",
              params: { orderId: order.id },
            });
          }}
          defaultSorting={[
            {
              id: "deliveryTime",
              desc: false,
            },
          ]}
          noDataMessage={t("PAGES.MY_ORDERS.TABLE.NO_DATA")}
        />
      )}
      {filteredOrders.length > 0 && (
        <>
          <DropdownMenu
            id="admin-orders-export"
            style={{ float: "right", marginTop: "var(--wlcm-spacing-sm)" }}
            activator={
              <Button secondary icon="file_export">
                {t("SNACK_ADMIN.EXPORT_ORDERS", {
                  count:
                    selectedOrdersCount > 0
                      ? selectedOrdersCount
                      : filteredOrders?.length,
                })}
              </Button>
            }
            menuOptions={[
              {
                icon: "csv",
                label: "CSV",
                onSelect: handleExport,
              },
              {
                icon: "microsoft_excel",
                label: "Microsoft Excel",
                onSelect: () => handleExport("excel"),
              },
              {
                icon: "print",
                label: "PDF",
                onSelect: () => handleExport("pdf"),
              },
            ]}
          />
          {/* Hidden printable content */}
          <div style={{ display: "none" }}>
            <PrintableOrders
              ref={printComponentRef}
              orders={pdfExportOrders}
              supplier={supplier}
              timeframe={timeframeFilter}
            />
          </div>
        </>
      )}
    </>
  );
}
